import { useQuery } from '@apollo/client';
import React, { useRef } from 'react';
import styled from 'styled-components';
import { useIBTranslation } from '@ibinder/ui-common-hooks';
import { ModalMenu } from '@ibinder/ui-common-dialogs';

import AdminModal from '../AdminModal';
import CreateCustomerForm from './CreateCustomerForm';
import CustomerInfo from './CustomerInfo';
import { GET_CUSTOMER } from '../../../../../../api/queries/queries_local';

const ModalWrapper = styled.div`
  padding: 1rem;
  height: 100%;
  overflow-x: auto;
`;

export enum CustomerModalNavTabs {
  CREATE = 'Create',
  INFO = 'Info'
}

interface CustomerViewModalProps {
  onClose: () => void;
  customerId?: string;
  type: string;
  isCreateCustomer?: boolean;
  setIsCreateCustomer: (value: boolean) => void;
  handleSetCustomer: (customerId: string) => void;
}

const CustomerViewModal: React.FC<CustomerViewModalProps> = ({
  onClose,
  customerId = '',
  type,
  isCreateCustomer = false,
  setIsCreateCustomer,
  handleSetCustomer
}) => {
  const showDoneModalNode = useRef();
  const { t } = useIBTranslation();
  const isInfoDirtyRef = useRef(false);

  const [item, setItem] = React.useState<string>(
    isCreateCustomer
      ? t(`CustomerMenu.${CustomerModalNavTabs.CREATE}`)
      : t(`CustomerMenu.${CustomerModalNavTabs.INFO}`)
  );

  const customer = useQuery(GET_CUSTOMER, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      customerId: customerId
    },
    skip: !customerId || customerId.length === 0
  });

  const menuItems = isCreateCustomer
    ? [CustomerModalNavTabs.CREATE]
    : [CustomerModalNavTabs.INFO];

  const loading = customer.loading;

  const handleInfoDirtyChange = (dirty: boolean) => {
    isInfoDirtyRef.current = dirty;
  };

  const confirmDiscardIfDirty = (currentTabLabel?: string) => {
    if (isInfoDirtyRef.current) {
      const tabName = currentTabLabel ?? item;
      const proceed = window.confirm(
        `You have unsaved changes in ${tabName}. Do you want to discard them?`
      );
      if (!proceed) {
        return false;
      }
      isInfoDirtyRef.current = false;
    }
    return true;
  };

  const onCloseModal = () => {
    if (!confirmDiscardIfDirty(item)) return;
    onClose();
  };

  const handleSelectTab = (e: string) => {
    if (!confirmDiscardIfDirty(e)) return;
    setItem(e);
  };

  return (
    <AdminModal
      ref={showDoneModalNode}
      onClose={onCloseModal}
      loading={loading}
      label={
        isCreateCustomer
          ? t(`CustomerMenu.${CustomerModalNavTabs.CREATE}`)
          : customer?.data?.getCustomer?.name ?? 'Loading...'
      }
      name={
        isCreateCustomer
          ? t(`CustomerMenu.${CustomerModalNavTabs.CREATE}`)
          : customer?.data?.getCustomer?.name ?? 'Loading...'
      }
      Component={() => (
        <>
          {isCreateCustomer ? (
            <CreateCustomerForm
              onClose={onClose}
              setIsCreateCustomer={setIsCreateCustomer}
              handleSetCustomer={handleSetCustomer}
            />
          ) : (
            <>
              <ModalMenu
                menuItems={menuItems.map((key) => t(`CustomerMenu.${key}`))}
                onSelect={handleSelectTab}
                selected={item}
              />
              <ModalWrapper>
                {item === t(`CustomerMenu.${CustomerModalNavTabs.INFO}`) && (
                  <CustomerInfo
                    customerData={customer?.data?.getCustomer}
                    onDirtyChange={handleInfoDirtyChange}
                  />
                )}
              </ModalWrapper>
            </>
          )}
        </>
      )}
    />
  );
};

export default CustomerViewModal;
